import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trophy, X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const EVENT_TYPES = [
  { key: "all",        label: "All" },
  { key: "goodies",    label: "🎁 Goodies" },
  { key: "award",      label: "🏆 Awards" },
  { key: "milestone",  label: "🚀 Milestones" },
  { key: "graduation", label: "🎓 Graduations" },
  { key: "event",      label: "📸 Events" },
];

const displayStack = { fontFamily: "'Outfit', system-ui, sans-serif" };

export default function WallOfFame() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null); // index of active item

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { event_type: filter };
      const { data } = await api.get("/wall-of-fame", { params: { ...params, limit: 200 } });
      setItems(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  useEffect(() => {
    document.title = "Wall of Fame — Real Student Stories | Code With Kamlesh";
  }, []);

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);
  const prev = () => setLightbox((i) => (i > 0 ? i - 1 : items.length - 1));
  const next = () => setLightbox((i) => (i < items.length - 1 ? i + 1 : 0));

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, items.length]);

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="wall-of-fame-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,0,102,0.15)_0%,_transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF0066]/15 border border-[#FF0066]/30 text-[#FF0066] text-[11px] uppercase tracking-wider font-bold">
            <Trophy className="w-3.5 h-3.5" /> Real Students · Real Stories
          </div>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold" style={displayStack}>
            Wall of <span className="text-[#FF0066]">Fame</span>
          </h1>
          <p className="mt-3 text-white/60 max-w-2xl mx-auto text-sm sm:text-base">
            Goodies receive karte hue moments, awards, graduations aur celebrations — humare students ke real wins.
          </p>
        </div>
      </section>

      {/* Filter chips */}
      <div className="sticky top-0 z-20 bg-[#050505]/95 backdrop-blur border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap gap-2">
          {EVENT_TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`inline-flex items-center rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                filter === t.key
                  ? "bg-[#FF0066] text-white"
                  : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
              }`}
              data-testid={`wof-filter-${t.key}`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/40">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-white/50">
            <Trophy className="w-12 h-12 mx-auto text-white/20 mb-3" />
            <div>No photos in this category yet — check back soon!</div>
          </div>
        ) : (
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4" data-testid="wof-grid">
            {items.map((entry, idx) => (
              <button
                key={entry.id}
                onClick={() => openLightbox(idx)}
                className="group relative w-full block break-inside-avoid rounded-xl overflow-hidden bg-[#0a0a0a] border border-white/10 hover:border-[#FF0066]/40 transition text-left"
                data-testid={`wof-tile-${entry.id}`}
              >
                <img
                  src={entry.image_url}
                  alt={entry.caption || "Student wall of fame"}
                  className="w-full block group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                />
                {(entry.caption || entry.student_name) && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition">
                    {entry.student_name && <div className="text-xs font-semibold text-white">{entry.student_name}</div>}
                    {entry.caption && <div className="text-[11px] text-white/80 line-clamp-2 mt-0.5">{entry.caption}</div>}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && items[lightbox] && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={closeLightbox} data-testid="wof-lightbox">
          <button onClick={closeLightbox} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Close"><X className="w-5 h-5 text-white" /></button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Previous"><ChevronLeft className="w-5 h-5 text-white" /></button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Next"><ChevronRight className="w-5 h-5 text-white" /></button>
          <div className="max-w-4xl w-full max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={items[lightbox].image_url} alt={items[lightbox].caption || ""} className="max-w-full max-h-[75vh] rounded-xl" />
            {(items[lightbox].caption || items[lightbox].student_name) && (
              <div className="mt-4 text-center max-w-2xl">
                {items[lightbox].student_name && <div className="text-base font-bold text-white">{items[lightbox].student_name}</div>}
                {items[lightbox].caption && <div className="text-sm text-white/70 mt-1">{items[lightbox].caption}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
